// effects/damage-numbers/manager.ts — Damage number classes and animation

import {
  DAMAGE_NUMBER_MAX_DAMAGE_FOR_SCALING,
  DAMAGE_NUMBER_MAX_FONT_SIZE,
  DAMAGE_NUMBER_MAX_MOVEMENT,
  DAMAGE_NUMBER_MIN_FONT_SIZE,
  DAMAGE_NUMBER_MIN_MOVEMENT,
  FONT_FAMILY_CANVAS,
} from "@game/config";

// DamageNumber class
export class DamageNumber {
  id = "";
  worldX = 0; // Store world coordinates
  worldY = 0; // Store world coordinates
  x = 0; // Store screen coordinates for overlap detection
  y = 0; // Store screen coordinates for overlap detection
  damage = 0;
  source = "";
  startTime = 0;
  startWorldX = 0; // Store starting world position
  startWorldY = 0; // Store starting world position
  yOffset = 0; // Track vertical animation offset
  active = false;
  age = 0;

  initialize(worldX: number, worldY: number, damage: number, source: string) {
    this.id = `dmg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.startWorldX = worldX;
    this.startWorldY = worldY;
    this.worldX = worldX;
    this.worldY = worldY;
    this.yOffset = 0;
    this.damage = damage;
    this.source = source;
    this.startTime = performance.now();
    this.age = 0;
    this.active = true;
  }

  reset() {
    this.active = false;
    this.age = 0;
  }
}

// AnimationRenderer - handles animation mathematics, Canvas rendering, and visual effects
export class AnimationRenderer {
  // Font cache: avoid recomputing font strings every frame
  private static fontCache = new Map<number, string>();

  static updateAnimation(damageNumber: DamageNumber, deltaTime: number) {
    if (!damageNumber.active) return;

    damageNumber.age += deltaTime;

    // Animation duration: 800ms as per PRD
    const animationDuration = 0.8; // 800ms

    if (damageNumber.age >= animationDuration) {
      damageNumber.active = false;
      return;
    }

    // Calculate animation progress (0 to 1)
    const progress = damageNumber.age / animationDuration;

    // Calculate movement distance based on damage amount for visual impact
    const movementScaleFactor = Math.min(
      1.0,
      Math.sqrt(damageNumber.damage) /
        Math.sqrt(DAMAGE_NUMBER_MAX_DAMAGE_FOR_SCALING),
    );
    const movementDistance =
      DAMAGE_NUMBER_MIN_MOVEMENT +
      (DAMAGE_NUMBER_MAX_MOVEMENT - DAMAGE_NUMBER_MIN_MOVEMENT) *
        movementScaleFactor;

    // Ease-out movement: numbers move vertically upward with scaled distance
    const easeOutProgress = 1 - (1 - progress) ** 3; // Cubic ease-out
    damageNumber.yOffset = -movementDistance * easeOutProgress;

    // Keep world position stable (no horizontal drift)
    damageNumber.worldX = damageNumber.startWorldX;
    damageNumber.worldY = damageNumber.startWorldY;
  }

  static renderDamageNumber(
    ctx: CanvasRenderingContext2D,
    damageNumber: DamageNumber,
    screenPos: { x: number; y: number },
  ) {
    if (!damageNumber.active) return;

    // Animation duration: 800ms as per PRD
    const animationDuration = 0.8; // 800ms
    const progress = Math.min(damageNumber.age / animationDuration, 1);

    // Linear fade: opacity transitions linearly from 1.0 to 0.0
    const opacity = 1.0 - progress;

    // Color differentiation by source (WCAG AA contrast)
    let color: string;
    if (damageNumber.source === "friendly_fire") {
      color = "#FFFFFF"; // White for friendly fire (0 damage)
    } else if (damageNumber.source === "bullet") {
      color = "#FF8C42"; // Orange for bullet damage on players
    } else if (damageNumber.source === "asteroid") {
      color = "#E8E8E8"; // White/light gray for asteroid damage
    } else {
      color = "#DC2626"; // Red for collision damage
    }

    // Calculate font size based on damage amount for visual impact
    // Scale using config values for damage number font sizing, capped at 1.0
    const scaleFactor = Math.min(
      1.0,
      Math.sqrt(damageNumber.damage) /
        Math.sqrt(DAMAGE_NUMBER_MAX_DAMAGE_FOR_SCALING),
    );
    const fontSize =
      DAMAGE_NUMBER_MIN_FONT_SIZE +
      (DAMAGE_NUMBER_MAX_FONT_SIZE - DAMAGE_NUMBER_MIN_FONT_SIZE) * scaleFactor;

    // Use cached font string to avoid recomputation
    const fs = Math.round(fontSize);
    let fontStr = AnimationRenderer.fontCache.get(fs);
    if (!fontStr) {
      fontStr = `bold ${fs}px ${FONT_FAMILY_CANVAS}`;
      AnimationRenderer.fontCache.set(fs, fontStr);
    }

    // Set font and text properties with cached font string
    ctx.font = fontStr;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Apply opacity
    ctx.globalAlpha = opacity;

    ctx.strokeStyle = "#000000";
    ctx.lineWidth = fontSize * 0.5;
    ctx.lineJoin = "round";
    ctx.miterLimit = 2;
    ctx.strokeText(damageNumber.damage.toString(), screenPos.x, screenPos.y);

    // Draw filled text on top
    ctx.fillStyle = color;
    ctx.fillText(damageNumber.damage.toString(), screenPos.x, screenPos.y);

    // Reset alpha
    ctx.globalAlpha = 1.0;
  }
}

// Global dependencies (will be set by client.ts)
let canvas: HTMLCanvasElement;
let worldToScreen: (x: number, y: number) => { x: number; y: number };

export function setDamageNumberDependencies(
  canvasEl: HTMLCanvasElement,
  worldToScreenFn: (x: number, y: number) => { x: number; y: number },
) {
  canvas = canvasEl;
  worldToScreen = worldToScreenFn;
}

// DamageNumberManager - central coordinator with object pooling
export class DamageNumberManager {
  private pool: DamageNumber[] = [];
  private activeDamageNumbers: DamageNumber[] = [];
  private maxSimultaneous = 800; // Maximum 800 simultaneous damage numbers

  constructor() {
    // Object pooling: Pre-allocate 20 damage number objects
    // Initialize object pool
    for (let i = 0; i < 20; i++) {
      this.pool.push(new DamageNumber());
    }
  }

  createDamageNumber(
    worldX: number,
    worldY: number,
    damage: number,
    source: string,
  ) {
    try {
      // Enforce maximum simultaneous limit
      if (this.activeDamageNumbers.length >= this.maxSimultaneous) {
        // Remove oldest damage number
        const oldestIndex = this.activeDamageNumbers.findIndex(
          (dn) => dn.active,
        );
        if (oldestIndex !== -1) {
          const oldestDamageNumber = this.activeDamageNumbers[oldestIndex];
          if (oldestDamageNumber) {
            oldestDamageNumber.reset();
            this.returnToPool(oldestDamageNumber);
            this.activeDamageNumbers.splice(oldestIndex, 1);
          }
        }
      }

      // Get damage number from pool
      let damageNumber = this.pool.pop();
      if (!damageNumber) {
        // Pool exhausted, create new one
        damageNumber = new DamageNumber();
      }

      // Initialize damage number with world coordinates
      damageNumber.initialize(worldX, worldY, damage, source);
      this.activeDamageNumbers.push(damageNumber);

      return damageNumber;
    } catch (error) {
      console.warn("Error creating damage number:", error);
      return null;
    }
  }

  returnToPool(damageNumber: DamageNumber) {
    if (this.pool.length < 20) {
      // Don't let pool grow too large
      damageNumber.reset();
      this.pool.push(damageNumber);
    }
  }

  updateAll(deltaTime: number) {
    // Update animations for all active damage numbers
    for (let i = this.activeDamageNumbers.length - 1; i >= 0; i--) {
      const damageNumber = this.activeDamageNumbers[i];
      if (!damageNumber) continue;
      AnimationRenderer.updateAnimation(damageNumber, deltaTime);

      // Remove finished animations
      if (!damageNumber.active) {
        this.returnToPool(damageNumber);
        this.activeDamageNumbers.splice(i, 1);
      }
    }
  }

  renderAll(canvasContext: CanvasRenderingContext2D) {
    // Render only damage numbers within viewport bounds
    for (const damageNumber of this.activeDamageNumbers) {
      if (damageNumber.active) {
        // Convert world coordinates to screen coordinates for rendering
        const screenPos = worldToScreen(
          damageNumber.worldX,
          damageNumber.worldY + damageNumber.yOffset,
        );

        // Store screen position for overlap detection
        damageNumber.x = screenPos.x;
        damageNumber.y = screenPos.y;

        // Viewport culling: only render if within canvas bounds with buffer
        if (
          screenPos.x >= -50 &&
          screenPos.x <= canvas.width + 50 &&
          screenPos.y >= -50 &&
          screenPos.y <= canvas.height + 50
        ) {
          AnimationRenderer.renderDamageNumber(
            canvasContext,
            damageNumber,
            screenPos,
          );
        }
      }
    }
  }

  getActiveCount() {
    return this.activeDamageNumbers.filter((dn) => dn.active).length;
  }
}
